import { Box, Grid, RadioGroup, Radio, FormControlLabel, MenuItem, Select, Tooltip, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useState, useCallback, useEffect, useRef } from "react";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";
import { getPreviousWeekday, getNextWeekday } from "../../../helpers";
import { DateSelectionMode } from "../../../constants";

/**
 * The "Bucket Inception" option (Strategy Report only). `range` is null while
 * it is loading or when none exists; `disabledReason` is the tooltip shown on
 * the disabled option when none exists (null while loading).
 */
export interface BucketInceptionOption {
  range: [string, string] | null;
  disabledReason: string | null;
}

interface DateSelectorProps {
  onDateChange?: (range: [string | null, string | null]) => void;
  defaultRange?: [string | null, string | null];
  style?: React.CSSProperties;
  additionalComponents?: React.ReactNode | null;
  inceptionDate?: string | null;
  removedDate?: string | null;
  bucketInception?: BucketInceptionOption;
  /**
   * Called with the Bucket Inception range when that option is picked, and
   * again whenever the range changes while it stays picked — its own channel
   * rather than `onDateChange`, because the report replaces every other
   * period with it instead of adding it. Keep its identity stable: it is an
   * effect dependency.
   */
  onBucketInceptionSelect?: (range: [string, string]) => void;
  /**
   * The option to start on (the Strategy Report's remembered pick); Year when
   * absent. A remembered Bucket Inception keeps the period the report already
   * holds: its range is not re-applied, and a range that is not there yet
   * does not reset it to Year, until the reader next touches this control or
   * `releaseRestoredPick` turns true.
   */
  defaultMode?: DateSelectionMode;
  /**
   * The report switched to another account or Bucket 3 after the page loaded:
   * the stored period no longer belongs to the option, so a remembered pick
   * behaves as a picked one again (takes the new range, or falls back to Year).
   */
  releaseRestoredPick?: boolean;
  /** Called with the option every time it changes, so the report can remember it. */
  onModeChange?: (mode: DateSelectionMode) => void;
  /**
   * Called when the reader picks a different option, before that option hands
   * over any range — so the report can drop the periods picked under the
   * previous one. Not called for the option the selector starts on, nor for a
   * fall-back it makes on its own, nor for Bucket Inception: its range goes
   * over `onBucketInceptionSelect`, which replaces every period in one step
   * (a clear here first would leave the report's state and its stored copy
   * chasing each other).
   */
  onModeSwitch?: () => void;
  /**
   * How the options are offered: a radio per option (default), or one
   * drop-down beside the date picker with the picked period(s) on the line
   * below — the Strategy Report's compact layout. `style` applies to the
   * radios only.
   */
  variant?: "radios" | "dropdown";
  /**
   * Called with whether Year is the picked option while its picker holds no
   * year (the Strategy Report's Calculate waits for one, task 6269). Passing
   * it also turns that empty picker's border red once the reader has
   * switched to Year or opened the picker.
   */
  onMissingYearChange?: (missing: boolean) => void;
  /**
   * Called with whether Range is the option and its picker holds no complete
   * range yet (task 6281), so the report can hold Calculate back until one is
   * picked. Passing it also outlines the picker in red once the reader leaves
   * it without completing a range. Keep its identity stable: it is an effect
   * dependency.
   */
  onMissingRangeChange?: (missing: boolean) => void;
  /**
   * The range the Range picker opens with when Range is the starting option
   * (the Strategy Report's restored period); it counts as picked.
   */
  initialPickedRange?: [string, string] | null;
  /**
   * Called with whether Month is the picked option while its picker holds no
   * month (the Strategy Report's Calculate waits for one, task 6280). Passing
   * it also turns that empty picker's border red at once -- on a remembered
   * Month too: period chips already listed do not count as a pick.
   */
  onMissingMonthChange?: (missing: boolean) => void;
}

const MODE_LABELS: Record<DateSelectionMode, string> = {
  [DateSelectionMode.YEAR]: "Year",
  [DateSelectionMode.MONTH]: "Month",
  [DateSelectionMode.RANGE]: "Range",
  [DateSelectionMode.BUCKET_INCEPTION]: "Bucket Inception",
  [DateSelectionMode.INCEPTION]: "Since Inception",
};

const DEFAULT_RANGE: [string | null, string | null] = [null, null];
const MIN_DATE = dayjs("2023-01-01");

const formatRange = ([startDate, endDate]: [string, string]) =>
  `${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format("MM/DD/YYYY")}`;

const DateSelector = ({ defaultRange, onDateChange, style, additionalComponents, inceptionDate, removedDate, bucketInception, onBucketInceptionSelect, defaultMode, onModeChange, onModeSwitch, releaseRestoredPick, variant = "radios", onMissingYearChange, onMissingRangeChange, initialPickedRange, onMissingMonthChange }: DateSelectorProps) => {
  const theme = useTheme();
  const [selectionMode, setSelectionMode] = useState<DateSelectionMode>(defaultMode ?? DateSelectionMode.YEAR);
  // Whether the Year picker holds a picked year, and whether the reader has
  // touched it (switched to Year, opened it) -- only then is an empty one red.
  const [isYearPicked, setIsYearPicked] = useState(false);
  const [isYearTouched, setIsYearTouched] = useState(false);
  const isYearMissing = selectionMode === DateSelectionMode.YEAR && !isYearPicked;
  // Whether the Month picker holds a picked month.
  const [isMonthPicked, setIsMonthPicked] = useState(false);
  const isMonthMissing = selectionMode === DateSelectionMode.MONTH && !isMonthPicked;
  // A month typed in full, added once the reader leaves the field: each
  // keystroke can make a valid month on the way (typing "12" passes through
  // January), and only the last one is the reader's pick.
  const typedMonthRef = useRef<Dayjs | null>(null);
  const isPickerOpenRef = useRef(false);
  const [range, setRange] = useState<[string | null, string | null]>(DEFAULT_RANGE);
  // The Range picker's own state: the range it opens with (until the option
  // is next changed, which remounts it empty), whether it holds a complete
  // range, and whether the reader has left it without one.
  // Dates, not strings: the picker would read a string in its MM/DD/YYYY format.
  const [openingRange, setOpeningRange] = useState<Date[] | undefined>(() =>
    defaultMode === DateSelectionMode.RANGE && initialPickedRange
      ? initialPickedRange.map((date) => dayjs(date).toDate())
      : undefined,
  );
  const [isRangeComplete, setIsRangeComplete] = useState(() => openingRange !== undefined);
  const [isRangeTouched, setIsRangeTouched] = useState(false);
  const isRangeMissing = selectionMode === DateSelectionMode.RANGE && !isRangeComplete;

  useEffect(() => {
    onMissingRangeChange?.(isRangeMissing);
  }, [isRangeMissing, onMissingRangeChange]);
  // A remembered Bucket Inception pick, not yet touched since the page opened:
  // the range still loading in (and the account behind it) must not replace
  // the stored period or drop the pick.
  const restoredBucketInceptionRef = useRef(defaultMode === DateSelectionMode.BUCKET_INCEPTION);
  if (releaseRestoredPick) restoredBucketInceptionRef.current = false;

  useEffect(() => {
    onModeChange?.(selectionMode);
  }, [selectionMode, onModeChange]);

  useEffect(() => {
    onMissingYearChange?.(isYearMissing);
  }, [isYearMissing, onMissingYearChange]);

  useEffect(() => {
    onMissingMonthChange?.(isMonthMissing);
  }, [isMonthMissing, onMissingMonthChange]);

  // The option's range can vanish while it is picked (another account or
  // Bucket 3): leave nothing checked-but-disabled with no picker beside it.
  // A range that is only reloading (no reason yet) keeps the pick, so the new
  // range can take its place once it arrives.
  const bucketInceptionUnavailable =
    !!bucketInception && !bucketInception.range && bucketInception.disabledReason !== null;
  useEffect(() => {
    if (bucketInceptionUnavailable && !restoredBucketInceptionRef.current) {
      setSelectionMode((mode) => {
        if (mode !== DateSelectionMode.BUCKET_INCEPTION) return mode;
        // The reader's pick changed under them: show what Year now needs.
        setIsYearTouched(true);
        return DateSelectionMode.YEAR;
      });
    }
  }, [bucketInceptionUnavailable, releaseRestoredPick]);

  // Picking the option — and any change of its range while it stays picked —
  // hands the range over, so the report always matches the option's label.
  const bucketInceptionStart = bucketInception?.range?.[0];
  const bucketInceptionEnd = bucketInception?.range?.[1];
  useEffect(() => {
    if (restoredBucketInceptionRef.current) return;
    if (
      selectionMode === DateSelectionMode.BUCKET_INCEPTION &&
      bucketInceptionStart &&
      bucketInceptionEnd
    ) {
      onBucketInceptionSelect?.([bucketInceptionStart, bucketInceptionEnd]);
    }
  }, [selectionMode, bucketInceptionStart, bucketInceptionEnd, onBucketInceptionSelect, releaseRestoredPick]);

  const handleModeChange = useCallback((mode: DateSelectionMode) => {
    restoredBucketInceptionRef.current = false;
    if (mode !== selectionMode && mode !== DateSelectionMode.BUCKET_INCEPTION) {
      onModeSwitch?.();
    }
    setSelectionMode(mode);
    // A new option remounts the picker empty.
    setIsYearPicked(false);
    setIsYearTouched(mode === DateSelectionMode.YEAR);
    setOpeningRange(undefined);
    setIsRangeComplete(false);
    setIsRangeTouched(false);
    setIsMonthPicked(false);
    typedMonthRef.current = null;
    if (mode === DateSelectionMode.INCEPTION && inceptionDate) {
      const endDate = removedDate
        ? removedDate
        : getPreviousWeekday(dayjs().format("YYYY-MM-DD")).format("YYYY-MM-DD");
      onDateChange?.([inceptionDate, endDate]);
    }
  }, [selectionMode, inceptionDate, removedDate, onDateChange, onModeSwitch]);

  const handleBucketInceptionReclick = useCallback(() => {
    restoredBucketInceptionRef.current = false;
    if (
      selectionMode === DateSelectionMode.BUCKET_INCEPTION &&
      bucketInceptionStart &&
      bucketInceptionEnd
    ) {
      onBucketInceptionSelect?.([bucketInceptionStart, bucketInceptionEnd]);
    }
  }, [selectionMode, bucketInceptionStart, bucketInceptionEnd, onBucketInceptionSelect]);

  const handleYearChange = useCallback((value: Dayjs | null) => {
    setIsYearPicked(!!value);
    if (!value) {
      setRange(DEFAULT_RANGE);
      onDateChange?.(DEFAULT_RANGE);
      return;
    }

    const startDate = value.format("YYYY") + "-01-01";
    let endDate = value.format("YYYY") + "-12-31";
    if (value.format("YYYY") === dayjs().year().toString()) {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    const adjStartDate = getNextWeekday(startDate);
    const adjEndDate = getPreviousWeekday(endDate);
    const newRange: [string, string] = [adjStartDate.format("YYYY-MM-DD"), adjEndDate.format("YYYY-MM-DD")];
    setRange(newRange);
    onDateChange?.(newRange);
    return;
  }, [onDateChange]);

  const handleMonthChange = useCallback((value: Dayjs | null) => {
    setIsMonthPicked(!!value);
    if (!value) {
      setRange(DEFAULT_RANGE);
      onDateChange?.(DEFAULT_RANGE);
      return;
    }

    const startDate = value.format("YYYY-MM") + "-01";
    let endDate = value.endOf('month').format("YYYY-MM-DD");
    if (value.format("YYYY-MM") === dayjs().format("YYYY-MM")) {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    const adjStartDate = getNextWeekday(startDate);
    const adjEndDate = getPreviousWeekday(endDate);
    const newRange: [string, string] = [adjStartDate.format("YYYY-MM-DD"), adjEndDate.format("YYYY-MM-DD")];
    setRange(newRange);
    onDateChange?.(newRange);
    return;
  }, [onDateChange]);

  const commitTypedMonth = useCallback(() => {
    const typed = typedMonthRef.current;
    typedMonthRef.current = null;
    if (typed) handleMonthChange(typed);
  }, [handleMonthChange]);

  const handleRangeChange = useCallback((dates: DateObject[]) => {
    setIsRangeComplete(dates.length === 2);
    if (dates.length === 2) {
      const [startDate, endDate] = dates;
      const adjStartDate = getNextWeekday(startDate.format("YYYY-MM-DD"));

      let adjEndDate = getPreviousWeekday(endDate.format("YYYY-MM-DD"));
      if (adjEndDate.format("YYYY-MM-DD") > dayjs().format("YYYY-MM-DD")) {
        adjEndDate = getPreviousWeekday(dayjs().format("YYYY-MM-DD"));
      }

      const newRange: [string, string] = [
        adjStartDate.format("YYYY-MM-DD"),
        adjEndDate.format("YYYY-MM-DD"),
      ];
      setRange(newRange);
      onDateChange?.(newRange);
      return;
    }

    setRange(DEFAULT_RANGE);
    onDateChange?.(DEFAULT_RANGE);
  }, [onDateChange]);

  const formatPreviousRange = useCallback(() => {
    if (!defaultRange?.[0] || !defaultRange?.[1]) return "Pick range";
    return `${dayjs(defaultRange[0]).format("MM/DD/YYYY")} - ${dayjs(defaultRange[1]).format("MM/DD/YYYY")}`;
  }, [defaultRange]);

  // The drop-down layout sits beside the Strategy Report's other selectors, so
  // its controls take MUI's small size (40px) to match the Bucket 3 drop-down.
  const isCompact = variant === "dropdown";
  const controlHeight = isCompact ? "40px" : "56px";

  const showRangeError = !!onMissingRangeChange && isRangeMissing && isRangeTouched;

  const datePicker = selectionMode === DateSelectionMode.BUCKET_INCEPTION ? null : selectionMode === DateSelectionMode.RANGE ? (
    <CustomDatePicker
      range
      value={openingRange}
      format="MM/DD/YYYY"
      onChange={handleRangeChange}
      // Judged on each close, so a new range started after a whole one is
      // not outlined while the reader is still picking it.
      onClose={() => setIsRangeTouched(!isRangeComplete)}
      dateSeparator=" - "
      style={{
        height: controlHeight,
        width: "200px",
        fontSize: "16px",
        ...(showRangeError && { borderColor: theme.palette.error.main, boxShadow: `0 0 0 1px ${theme.palette.error.main}` }),
      }}
      // The picker forwards no aria-* props to its input, so the error state is
      // marked by a class beside its own.
      inputClass={showRangeError ? "rmdp-input rmdp-input-error" : "rmdp-input"}
      placeholder={formatPreviousRange()}
      showOtherDays
      calendarPosition="top"
      portal
      zIndex={1700}
      aria-label="Select date range"
    />
  ) : (
    <DatePicker
      key={selectionMode}
      views={selectionMode === DateSelectionMode.YEAR ? [DateSelectionMode.YEAR] : [DateSelectionMode.YEAR, DateSelectionMode.MONTH]}
      format={selectionMode === DateSelectionMode.YEAR ? "YYYY" : "MM/YYYY"}
      onAccept={selectionMode === DateSelectionMode.YEAR ? handleYearChange : handleMonthChange}
      // Clearing the field un-picks the year or month. Where a missing one
      // blocks Calculate, one typed in full counts as picked too (a month
      // once the reader leaves the field), so the field never shows a period
      // the report says is missing.
      onChange={(value: Dayjs | null) => {
        if (!value || !value.isValid()) {
          setIsYearPicked(false);
          setIsMonthPicked(false);
          typedMonthRef.current = null;
        } else if (
          onMissingYearChange &&
          selectionMode === DateSelectionMode.YEAR &&
          !value.isBefore(MIN_DATE, "year")
        ) {
          handleYearChange(value);
        } else if (
          onMissingMonthChange &&
          selectionMode === DateSelectionMode.MONTH &&
          // The popup's own steps (its year view moves the value too) are
          // left to onAccept; only typing reaches here with it closed.
          !isPickerOpenRef.current
        ) {
          if (value.isBefore(MIN_DATE, "month")) {
            typedMonthRef.current = null;
            setIsMonthPicked(false);
          } else {
            typedMonthRef.current = value;
          }
        }
      }}
      onOpen={() => {
        isPickerOpenRef.current = true;
        setIsYearTouched(true);
      }}
      onClose={() => {
        isPickerOpenRef.current = false;
      }}
      minDate={MIN_DATE}
      sx={{ height: controlHeight, width: "200px" }}
      slotProps={{
        textField: {
          size: isCompact ? "small" : "medium",
          error:
            (!!onMissingYearChange && isYearMissing && isYearTouched) ||
            (!!onMissingMonthChange && isMonthMissing),
          placeholder: defaultRange ? defaultRange[0]?.split("-")[0] : `Pick ${selectionMode}`,
          "aria-label": `Select ${selectionMode}`,
          onBlur: commitTypedMonth,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === "Enter") commitTypedMonth();
          },
        },
        popper: {
          sx: { zIndex: 1700 },
        },
      }}
    />
  );

  if (variant === "dropdown") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingY: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <Select
            value={selectionMode}
            onChange={(event) => handleModeChange(event.target.value as DateSelectionMode)}
            renderValue={(mode) => MODE_LABELS[mode]}
            inputProps={{ "aria-label": "date selection mode" }}
            MenuProps={{ sx: { zIndex: 1700 } }}
            size="small"
            sx={{ width: "200px" }}
          >
            <MenuItem value={DateSelectionMode.YEAR}>Year</MenuItem>
            <MenuItem value={DateSelectionMode.MONTH}>Month</MenuItem>
            <MenuItem value={DateSelectionMode.RANGE}>Range</MenuItem>
            {bucketInception && (
              <MenuItem
                value={DateSelectionMode.BUCKET_INCEPTION}
                disabled={!bucketInception.range}
                // Re-picking the already-picked option fires no change, so
                // it re-applies the range here (e.g. after its chip was removed).
                onClick={handleBucketInceptionReclick}
              >
                <Tooltip title={!bucketInception.range ? bucketInception.disabledReason ?? "" : ""} placement="left" describeChild>
                  {/* A disabled item takes no pointer events; this span takes the
                      hover back, and keeps its click from reaching the item. */}
                  <span
                    // Fills the item (MenuItem pads 6px 16px) so its whole row hovers.
                    style={{ pointerEvents: "auto", flex: 1, margin: "-6px -16px", padding: "6px 16px" }}
                    onClick={(event) => {
                      if (!bucketInception.range) event.stopPropagation();
                    }}
                  >
                    {MODE_LABELS[DateSelectionMode.BUCKET_INCEPTION]}
                  </span>
                </Tooltip>
              </MenuItem>
            )}
            {inceptionDate && (
              <MenuItem value={DateSelectionMode.INCEPTION}>Since Inception</MenuItem>
            )}
          </Select>
          {datePicker}
        </Box>
        {additionalComponents}
      </Box>
    );
  }

  return (
    <Grid container sx={{ paddingY: 1, paddingLeft: 1, justifyContent: style ? 'flex-start' : 'flex-end' }} alignItems={style ? 'flex-start' : 'center'}>
      <Grid item marginRight={2}>
        <RadioGroup
          row
          value={selectionMode}
          onChange={(event) => handleModeChange(event.target.value as DateSelectionMode)}
          aria-label="date selection mode"
          sx={style ? { ...style } : {}}
        >
          <FormControlLabel value={DateSelectionMode.YEAR} control={<Radio />} label="Year" />
          <FormControlLabel value={DateSelectionMode.MONTH} control={<Radio />} label="Month" />
          <FormControlLabel value={DateSelectionMode.RANGE} control={<Radio />} label="Range" />
          {bucketInception && (
            <Tooltip
              title={!bucketInception.range ? bucketInception.disabledReason ?? "" : ""}
              placement="left"
            >
              {/* A disabled control fires no mouse events; the span carries the hover. */}
              <span style={{ display: "inline-flex" }}>
                <FormControlLabel
                  value={DateSelectionMode.BUCKET_INCEPTION}
                  // Re-clicking the already-picked option fires no change, so
                  // it re-applies the range here (e.g. after its chip was removed).
                  control={<Radio onClick={handleBucketInceptionReclick} />}
                  disabled={!bucketInception.range}
                  label={bucketInception.range
                    ? `Bucket Inception (${formatRange(bucketInception.range)})`
                    : "Bucket Inception"}
                />
              </span>
            </Tooltip>
          )}
          {inceptionDate && (
            <FormControlLabel value={DateSelectionMode.INCEPTION} control={<Radio />} label="Since Inception" />
          )}
        </RadioGroup>
      </Grid>
      <Grid item marginRight={2}>
        {datePicker}
      </Grid>
      <Grid item>{additionalComponents}</Grid>
    </Grid>
  );
};

export default DateSelector;
